import { BlobCtaButton, CardGlass, GlassChip, Modal, Typography, useToast } from "@cupcodev/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CustomerUpgradeOffer } from "../../shared/offers/offers-projection";
import type { CustomerPlanUpgrade, UpgradeEligibility } from "../../shared/upgrades/upgrades";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";
import { NaveIcon, Notice } from "../../ui/NavePage";
import { UpgradeProgress, isUpgradeInFlight } from "./UpgradeProgress";

/**
 * Sidebar promotion of the next package tier for a contract the customer already has, and the
 * self-service flow behind it: contract by e-mail, signature, payment of the pro-rata amount, plan active.
 * Every value (prices, quote, contract, invoice) comes from the owners (Stargate and Cashier); nothing here
 * computes money. When self-service is not possible the request still goes to Support, as before.
 */
export const UpgradeOfferCard = () => {
  const { runtime, session, activeOrganizationId } = useScenario();
  const { t, formatCurrency, formatDate } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [offers, setOffers] = useState<readonly CustomerUpgradeOffer[]>([]);
  const [latest, setLatest] = useState<CustomerPlanUpgrade | null>(null);
  const [eligibility, setEligibility] = useState<UpgradeEligibility>();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string>();
  const [sendFailed, setSendFailed] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const clientId = session?.activeClient?.id;

  useEffect(() => {
    let cancelled = false;
    setOffers([]);
    if (!clientId || !runtime.experience.listUpgradeOffers) return;
    void runtime.experience.listUpgradeOffers().then((result) => {
      if (!cancelled && result.ok) setOffers(result.value);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [runtime, clientId, activeOrganizationId.value]);

  const refreshLatest = useCallback(async (): Promise<void> => {
    if (!runtime.experience.getLatestUpgrade) return;
    const result = await runtime.experience.getLatestUpgrade().catch(() => undefined);
    if (result?.ok) setLatest(result.value);
  }, [runtime]);

  useEffect(() => {
    setLatest(null);
    if (clientId) void refreshLatest();
  }, [clientId, activeOrganizationId.value, refreshLatest]);

  // While an upgrade waits for a signature or a payment, follow it so the screen moves on by itself.
  const waiting = latest?.status === "awaiting_signature" || latest?.status === "awaiting_payment";
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => { void refreshLatest(); }, open ? 8_000 : 30_000);
    return () => window.clearInterval(timer);
  }, [waiting, open, refreshLatest]);

  const offer = offers[0];
  const offerId = offer?.offerId;
  useEffect(() => {
    setEligibility(undefined);
    if (!open || !offerId || !runtime.experience.getUpgradeEligibility) return;
    let cancelled = false;
    void runtime.experience.getUpgradeEligibility(offerId).then((result) => { if (!cancelled && result.ok) setEligibility(result.value); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [open, offerId, runtime]);

  const inFlight = isUpgradeInFlight(latest) ? latest : undefined;
  if (!offer && !inFlight) return null;

  const canRequest = Boolean(runtime.experience.submitSupport)
    && session?.grants.some((grant) => grant.organizationId === activeOrganizationId.value && grant.capability === "support.create"
      && grant.scopeKind === "organization" && grant.scopeRef === activeOrganizationId.value) === true;

  // ---- progress of an upgrade already started
  if (inFlight) {
    const params = { service: inFlight.quote.serviceName, target: inFlight.quote.targetLabel, current: inFlight.quote.currentLabel, amount: formatCurrency(inFlight.quote.amountDueToday, inFlight.quote.currency) };
    const send = async (): Promise<void> => {
      if (busy || !runtime.experience.sendUpgrade) return;
      setBusy(true); setSendFailed(false);
      try {
        const result = await runtime.experience.sendUpgrade(inFlight.upgradeId);
        if (result.ok) { setLatest(result.value); toast({ variant: "success", title: t("upgrade.progress.sent") }); } else setSendFailed(true);
      } catch { setSendFailed(true); } finally { setBusy(false); }
    };
    const pay = (): void => {
      if (inFlight.checkoutUrl) window.open(inFlight.checkoutUrl, "_blank", "noopener,noreferrer");
      else if (inFlight.invoiceId) { setOpen(false); navigate(`/billing/invoices/${encodeURIComponent(inFlight.invoiceId)}`); }
    };
    const headline = inFlight.status === "active" ? "upgrade.status.active" : inFlight.status === "awaiting_payment" ? "upgrade.status.pay" : "upgrade.status.sign";
    const cta = inFlight.status === "active" ? "upgrade.status.ctaActive" : inFlight.status === "awaiting_payment" ? "upgrade.status.ctaPay" : "upgrade.status.ctaSign";
    return <>
      <CardGlass variant="glass-sm" elevation={0} className="nave-upgrade-card" data-status={inFlight.status}>
        <GlassChip variant={inFlight.status === "active" ? "success" : "purple"}>{t(inFlight.status === "active" ? "upgrade.status.badgeActive" : "upgrade.status.badge")}</GlassChip>
        <Typography as="p" variant="title-sm">{t(headline, params)}</Typography>
        <Typography as="p" variant="body-sm" tone="muted">{t(`${headline}Body`, params)}</Typography>
        <BlobCtaButton type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>{t(cta)}</BlobCtaButton>
      </CardGlass>
      <Modal open={open} onOpenChange={setOpen} title={t("upgrade.modal.title", params)} description={t("upgrade.modal.description", params)} size="md" layout="compact" showCloseButton
        footer={<div className="nave-modal-actions"><BlobCtaButton type="button" size="sm" variant="secondary" onClick={() => setOpen(false)}>{t("upgrade.progress.close")}</BlobCtaButton></div>}>
        <UpgradeProgress upgrade={inFlight} busy={busy} sendFailed={sendFailed} onResend={() => void send()} onPay={pay} />
      </Modal>
    </>;
  }

  // ---- the offer
  if (!offer) return null;
  const suffix = offer.cadence === "monthly" ? t("upgrade.perMonth") : offer.cadence === "yearly" ? t("upgrade.perYear") : "";
  const money = (amount: string): string => `${formatCurrency(amount, offer.currency)}${suffix}`;
  const params = { service: offer.serviceName, current: offer.current.label, target: offer.target.label, price: money(offer.target.amount), difference: money(offer.differenceAmount) };
  const quote = offer.proration;
  const quoteParams = quote ? { amount: formatCurrency(quote.amountDueToday, offer.currency), remaining: quote.remainingDays, total: quote.totalDays,
    date: formatDate(`${quote.nextCycleDate}T12:00:00Z`) } : undefined;
  const selfService = eligibility?.selfService === true && quote !== undefined;

  const errorKey = (code: string): string => code === "QUOTE_CHANGED" ? "upgrade.self.error.changed"
    : code === "UPGRADE_IN_PROGRESS" ? "upgrade.self.error.inProgress"
      : code === "LEGAL_REPRESENTATIVE_MISSING" ? "upgrade.self.error.noRepresentative"
        : code.startsWith("OWNER_CONTRACT") ? "upgrade.self.error.contract" : "upgrade.error";

  const startSelfService = async (): Promise<void> => {
    if (busy || !quote || !runtime.experience.startUpgrade) return;
    setBusy(true); setFailed(undefined); setSendFailed(false);
    try {
      const started = await runtime.experience.startUpgrade({ offerId: offer.offerId, requestId: requestId.current, expectedAmount: quote.amountDueToday });
      if (!started.ok) {
        setFailed(started.error.code);
        if (started.error.code === "QUOTE_CHANGED") { requestId.current = crypto.randomUUID(); const fresh = await runtime.experience.listUpgradeOffers?.(); if (fresh?.ok) setOffers(fresh.value); }
        return;
      }
      setLatest(started.value);
      const sent = await runtime.experience.sendUpgrade?.(started.value.upgradeId);
      if (sent?.ok) { setLatest(sent.value); toast({ variant: "success", title: t("upgrade.progress.sent") }); } else setSendFailed(true);
    } catch { setFailed("DEPENDENCY_UNAVAILABLE"); } finally { setBusy(false); }
  };

  const request = async (): Promise<void> => {
    if (busy || !runtime.experience.submitSupport) return;
    setBusy(true);
    setFailed(undefined);
    try {
      const options = await runtime.experience.supportOptions?.();
      const list = options?.ok ? options.value : [];
      const wanted = offer.serviceName.toLocaleLowerCase();
      const entitlementId = (list.find((option) => option.label.toLocaleLowerCase().includes(wanted)) ?? list[0])?.id;
      if (!entitlementId) { setFailed("DEPENDENCY_UNAVAILABLE"); return; }
      const result = await runtime.experience.submitSupport({
        requestId: crypto.randomUUID(), entitlementId,
        subject: t("upgrade.request.subject", params),
        text: quoteParams ? `${t("upgrade.request.text", params)} ${t("upgrade.request.quote", quoteParams)}` : t("upgrade.request.text", params),
      });
      if (!result.ok) { setFailed("DEPENDENCY_UNAVAILABLE"); return; }
      setOpen(false);
      toast({ variant: "success", title: t("upgrade.sent") });
      navigate(`/support/tickets/${encodeURIComponent(result.value.ticketId)}`);
    } catch { setFailed("DEPENDENCY_UNAVAILABLE"); } finally { setBusy(false); }
  };

  const steps = selfService
    ? ["upgrade.self.step1", "upgrade.self.step2", "upgrade.self.step3", "upgrade.self.step4"]
    : ["upgrade.modal.step1", "upgrade.modal.step2", quoteParams ? "upgrade.modal.step3Quote" : "upgrade.modal.step3"];

  return <>
    <CardGlass variant="glass-sm" elevation={0} className="nave-upgrade-card">
      <GlassChip variant="purple">{t("upgrade.badge")}</GlassChip>
      <Typography as="p" variant="title-sm">{t("upgrade.card.title", params)}</Typography>
      <Typography as="p" variant="body-sm" tone="muted">{t("upgrade.card.body", params)}</Typography>
      <BlobCtaButton type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>{t("upgrade.cta")}</BlobCtaButton>
    </CardGlass>
    <Modal open={open} onOpenChange={(value) => { if (!busy) setOpen(value); }} title={t("upgrade.modal.title", params)} description={t("upgrade.modal.description", params)}
      size="md" layout="compact" showCloseButton
      footer={<div className="nave-modal-actions">
        <BlobCtaButton type="button" size="sm" variant="secondary" disabled={busy} onClick={() => setOpen(false)}>{t("upgrade.modal.cancel")}</BlobCtaButton>
        {selfService ? <BlobCtaButton type="button" size="sm" loading={busy} onClick={() => void startSelfService()}>{t("upgrade.self.cta")}</BlobCtaButton>
          : canRequest ? <BlobCtaButton type="button" size="sm" loading={busy} onClick={() => void request()}>{t("upgrade.modal.submit")}</BlobCtaButton> : null}
      </div>}>
      <div className="nave-upgrade">
        <div className="nave-upgrade-compare">
          <div className="nave-upgrade-plan">
            <span className="nave-upgrade-plan-label">{t("upgrade.modal.yourPlan")}</span>
            <strong className="nave-upgrade-plan-name">{offer.current.label}</strong>
            <span className="nave-upgrade-plan-price">{money(offer.current.amount)}</span>
          </div>
          <span className="nave-upgrade-arrow" aria-hidden="true"><NaveIcon name="lucide:arrow-right" /></span>
          <div className="nave-upgrade-plan nave-upgrade-plan--target">
            <span className="nave-upgrade-plan-label">{t("upgrade.modal.new")}</span>
            <strong className="nave-upgrade-plan-name">{offer.target.label}</strong>
            <span className="nave-upgrade-plan-price">{money(offer.target.amount)}</span>
          </div>
        </div>
        {offer.includes ? <UpgradeIncludes includes={offer.includes} target={offer.target.label} /> : null}
        {quote && quoteParams ? <div className="nave-upgrade-quote">
          <div className="nave-upgrade-quote-row nave-upgrade-quote-row--now">
            <span><strong>{t("upgrade.quote.now")}</strong><small>{t("upgrade.quote.nowDetail", quoteParams)}</small></span>
            <strong className="nave-upgrade-quote-amount">{quoteParams.amount}</strong>
          </div>
          <div className="nave-upgrade-quote-row">
            <span><strong>{t("upgrade.quote.from", quoteParams)}</strong><small>{t("upgrade.quote.fromDetail", params)}</small></span>
            <strong className="nave-upgrade-quote-amount">{money(offer.target.amount)}</strong>
          </div>
        </div> : <div className="nave-upgrade-diff">
          <span>{t("upgrade.modal.difference")}</span>
          <strong>+{money(offer.differenceAmount)}</strong>
        </div>}
        {selfService || canRequest ? <div className="nave-upgrade-how">
          <span className="nave-upgrade-how-title">{t("upgrade.modal.how")}</span>
          <ol>{steps.map((key) => <li key={key}>{t(key, { ...quoteParams, target: offer.target.label, email: eligibility?.signer?.email ?? "" })}</li>)}</ol>
        </div> : <Notice>{t("upgrade.modal.noPermission")}</Notice>}
        {failed ? <Notice tone="warning">{t(errorKey(failed))}</Notice> : null}
        {sendFailed ? <Notice tone="warning">{t("upgrade.progress.signSendFailed", { email: "" })}</Notice> : null}
      </div>
    </Modal>
  </>;
};

/** Plan items from the Stargate catalog: what is new in the target tier first, then what carries over. */
const UpgradeIncludes = ({ includes, target }: { includes: NonNullable<CustomerUpgradeOffer["includes"]>; target: string }) => {
  const { t } = useI18n();
  const added = includes.items.filter((item) => item.isNew);
  const kept = includes.items.filter((item) => !item.isNew);
  const compare = includes.comparedWithCurrent && added.length > 0;
  return (
    <section className="nave-upgrade-includes" aria-label={t(compare ? "upgrade.includes.titleCompare" : "upgrade.includes.titleAll", { target })}>
      <span className="nave-upgrade-how-title">{t(compare ? "upgrade.includes.titleCompare" : "upgrade.includes.titleAll", { target })}</span>
      <ul className="nave-upgrade-list">
        {(compare ? added : includes.items).map((item) => (
          <li key={item.label} data-new={compare ? "true" : "false"}>
            <NaveIcon name={compare ? "lucide:sparkles" : "lucide:check"} />
            <span>{item.label}</span>
            {compare ? <span className="nave-upgrade-new">{t("upgrade.includes.new")}</span> : null}
          </li>
        ))}
      </ul>
      {compare && kept.length > 0 ? <details className="nave-upgrade-kept">
        <summary><NaveIcon name="lucide:chevron-right" />{t("upgrade.includes.keptCount", { count: kept.length })}</summary>
        <ul className="nave-upgrade-list nave-upgrade-list--kept">
          {kept.map((item) => <li key={item.label}><NaveIcon name="lucide:check" /><span>{item.label}</span></li>)}
        </ul>
      </details> : null}
    </section>
  );
};
